import { NextPage } from 'next'
import Head from 'next/head'
import styled from 'styled-components'

import ViewListing from 'modules/ViewListing'

import ErrorBox from 'components/ErrorBox'

import ApiCreator from 'services/api'

import {
  formatAddress,
  getMetaDescriptionFromListing,
  getSocialShareTextFromListing,
} from 'utils/listingHelpers'
import { isProduction } from 'utils/nodeEnv'

import * as T from 'types'

const logoUrl = `${process.env.S3_BASE_URL}/assets/Homelister-Logo-Blue.png`

const InvisibleError = styled.div`
  display: none;
`

interface IProps {
  listing?: T.IListing
  photos?: T.IPhoto[]
  listingError?: string | boolean
  photosError?: string | boolean
  id?: string
}

const ListingPage: NextPage<IProps> = ({ listing, photos, listingError, photosError, id }) => {
  if (!listing || listingError) {
    return (
      <>
        <InvisibleError>{`id ${id}: ${listingError}`}</InvisibleError>
        <ErrorBox error="There was a problem loading your listing" />
      </>
    )
  }

  if (listing.isHomeRise) {
    return <ErrorBox error="Not a HomeLister listing" />
  }

  if (photosError && !isProduction()) {
    // logging the error is the best we can do here
    /* eslint-disable no-console */
    console.error('There was an error fetching the public listing photos')
    if (typeof photosError === 'string') console.error(photosError)
    /* eslint-enable no-console */
  }

  const canonicalUrl = `https://www.homelister.com/listing/${listing.customLinkUrl}`
  return (
    <>
      <Head>
        <title>{`${formatAddress(listing, true)} ${listing.mlsList[0].number} - HomeLister`}</title>
        <meta
          name="description"
          content={getMetaDescriptionFromListing(listing)}
        />
        <meta
          name="og:title"
          property="og:title"
          content="Check out this beautiful home on HomeLister.com! #homelister"
        />
        <meta
          name="og:url"
          property="og:url"
          content={canonicalUrl}
        />
        <meta
          name="og:image"
          property="og:image"
          content={(photos as T.IPhoto[])[0]?.url ?? logoUrl}
        />
        <meta
          name="og:description"
          property="og:description"
          content={getSocialShareTextFromListing(listing)}
        />
        <meta
          name="og:site_name"
          property="og:site_name"
          content="HomeLister"
        />
      </Head>
      <ViewListing
        listing={listing}
        photos={photos || []}
        isPreview={false}
      />
    </>
  )
}

export default ListingPage

export function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

// We opt in to Incremental Static Regeneration by passing a `revalidate` prop
// Next.js will attempt to re-generate the page:
// - When a request comes in
// - At most once every second
export async function getStaticProps(ctx: any) {
  const id = ctx.params.id as string
  const api = new ApiCreator(ctx)

  const [listingResponse, photosResponse] = await Promise.all([
    api.getPublicListing(id),
    api.getPublicListingPhotos(id),
  ])

  if (!listingResponse.ok || !photosResponse.ok) {
    return {
      notFound: true,
      props: {
        listingError: listingResponse?.originalError?.message ?? !listingResponse.ok,
        photosError: photosResponse?.originalError?.message ?? !photosResponse.ok,
        id: ctx.params.id,
      },
      revalidate: 1, // In seconds
    }
  }

  return {
    props: {
      listing: listingResponse.data,
      photos: photosResponse.data,
    },
    revalidate: 1, // In seconds
  }
}
